import { CreditCard, ShieldCheck, Percent, EyeOff, CalendarX2, Sparkles, Infinity as InfinityIcon } from "lucide-react";

// Bloc tarifaire réutilisable de l'offre fondateur de lancement.
// variant: "light" (fond clair) ou "dark" (fond sombre / dégradé).
const GUARANTEES = [
  { icon: CreditCard, label: "Aucune carte bancaire" },
  { icon: ShieldCheck, label: "Sans engagement" },
  { icon: Percent, label: "Aucune commission" },
  { icon: EyeOff, label: "Aucun frais caché" },
  { icon: CalendarX2, label: "Aucun prélèvement automatique" },
];

// Rappel tarifaire compact (19,90 € barré → 0 € / 12 mois) pour bandeaux & lignes inline.
export const FounderPriceInline = ({ className = "", dark = false }) => (
  <span className={`inline-flex items-baseline gap-1 ${className}`} data-testid="founder-price-inline">
    <span className={`line-through ${dark ? "text-white/60" : "text-slate-400"}`}>19,90 €</span>
    <b className={dark ? "text-emerald-300" : "text-[#E05638]"}>0 €</b>
    <span>pendant 12 mois</span>
  </span>
);

export const FounderPriceBlock = ({ variant = "light", className = "" }) => {
  const dark = variant === "dark";
  const t = {
    wrap: dark
      ? "bg-white/10 backdrop-blur border border-white/20 text-white"
      : "bg-white border border-slate-200 text-slate-900 shadow-sm",
    badge: dark ? "bg-white/15 text-white" : "bg-amber-100 text-amber-800",
    strike: dark ? "text-white/60" : "text-slate-400",
    zero: dark ? "text-emerald-300" : "text-[#E05638]",
    period: dark ? "text-white/90" : "text-slate-600",
    sub: dark ? "text-white/80" : "text-slate-600",
    chip: dark ? "bg-white/10 border-white/20 text-white" : "bg-emerald-50 border-emerald-200 text-slate-800",
    chipIcon: dark ? "text-emerald-300" : "text-emerald-600",
    punch: dark ? "bg-white/10 border-white/20 text-white" : "bg-[#E05638]/5 border-[#E05638]/20 text-slate-800",
    free: dark ? "bg-emerald-400/15 border-emerald-300/30 text-white" : "bg-emerald-50 border-emerald-200 text-slate-800",
    freeIcon: dark ? "text-emerald-300" : "text-emerald-600",
  };

  return (
    <div className={`rounded-2xl p-5 md:p-6 ${t.wrap} ${className}`} data-testid="founder-price-block">
      <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest ${t.badge}`}>
        <Sparkles size={12} /> Offre de lancement — Partenaire Fondateur
      </div>

      <div className="mt-4 flex flex-wrap items-end gap-x-3 gap-y-1">
        <span className={`font-heading text-xl font-bold line-through ${t.strike}`} data-testid="founder-price-strike">19,90 €</span>
        <span className={`font-heading text-5xl font-extrabold leading-none ${t.zero}`} data-testid="founder-price-zero">0 €</span>
        <span className={`text-base font-bold ${t.period}`}>Pendant 12 mois</span>
      </div>

      <p className={`mt-3 text-sm ${t.sub}`}>
        Puis seulement <b>19,90 €/mois</b> si vous choisissez de continuer. Aucune carte bancaire.
      </p>

      <div className="mt-4 grid sm:grid-cols-2 gap-2">
        {GUARANTEES.map((g) => (
          <div key={g.label} className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold ${t.chip}`}>
            <g.icon size={16} className={`shrink-0 ${t.chipIcon}`} />
            <span>{g.label}</span>
          </div>
        ))}
      </div>

      <div className={`mt-4 rounded-xl border p-3 text-sm font-semibold ${t.punch}`}>
        Après 12 mois, vous décidez ! Aucun renouvellement imposé.
      </div>

      <div className={`mt-3 flex items-start gap-2 rounded-xl border p-3 text-sm font-semibold ${t.free}`} data-testid="founder-free-forever">
        <InfinityIcon size={16} className={`shrink-0 mt-0.5 ${t.freeIcon}`} />
        <span>Et même après, les services de base restent <b>gratuits à vie</b> : votre fiche locale référencée sur Google et la publication sur nos groupes Facebook.</span>
      </div>
    </div>
  );
};

export default FounderPriceBlock;
